// ============================================================================
// PiiAccessLogTab — registre audit.PiiAccessLog (JOURNAL-LOI25, lecture seule)
// ============================================================================

import { useId, useState } from 'react';
import {
  DataTable, EmptyState, QueryError, SkeletonCard, type Column,
} from '@gs/ui';
import { Download, FileText, Shield } from 'lucide-react';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { SectionHeader } from './SectionHeader';
import { GS_COMPONENTS } from '@gs/ui';
import { formatDateTimeFr } from '../../lib/format/date';
import {
  downloadPiiAccessLogExport,
  usePiiAccessLog,
  usePiiAccessLogRetentionMutation,
  type PiiAccessLogEntry,
  type PiiAccessLogFiltersState,
} from '../../hooks/usePiiAccessLog';

const PAGE_SIZE = 50;

export function PiiAccessLogTab({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<PiiAccessLogFiltersState>({});
  const [draft, setDraft] = useState<PiiAccessLogFiltersState>({});
  const [retentionDraft, setRetentionDraft] = useState<string>('');
  const [exportError, setExportError] = useState<'csv' | 'pdf' | null>(null);
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null);

  const idFrom = useId();
  const idTo = useId();
  const idActor = useId();
  const idSubject = useId();
  const idField = useId();
  const idRecordType = useId();
  const idRetention = useId();

  const q = usePiiAccessLog(enabled, filters, page, PAGE_SIZE);
  const retentionMut = usePiiAccessLogRetentionMutation();

  if (q.isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <QueryError message={t('gest:admin.piiAccessLog.loadError')} onRetry={() => void q.refetch()} />
    );
  }

  const { items, total, retentionYears, retentionMinYears } = q.data;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns: Column<PiiAccessLogEntry>[] = [
    {
      key: 'accessedAt',
      header: t('gest:admin.piiAccessLog.columns.accessedAt'),
      render: (row) => (
        <time dateTime={row.accessedAt}>{formatDateTimeFr(row.accessedAt)}</time>
      ),
    },
    {
      key: 'actorEmail',
      header: t('gest:admin.piiAccessLog.columns.actor'),
      render: (row) => row.actorEmail ?? '—',
    },
    {
      key: 'fieldName',
      header: t('gest:admin.piiAccessLog.columns.field'),
      render: (row) => row.fieldName,
    },
    {
      key: 'recordType',
      header: t('gest:admin.piiAccessLog.columns.recordType'),
      render: (row) => row.recordType,
    },
    {
      key: 'subjectLabel',
      header: t('gest:admin.piiAccessLog.columns.subject'),
      render: (row) => row.subjectLabel,
    },
    {
      key: 'reason',
      header: t('gest:admin.piiAccessLog.columns.reason'),
      render: (row) => row.reason ?? '—',
    },
    {
      key: 'ipAddress',
      header: t('gest:admin.piiAccessLog.columns.ip'),
      render: (row) => row.ipAddress ?? '—',
    },
  ];

  function applyFilters() {
    setFilters({ ...draft });
    setPage(1);
  }

  function clearFilters() {
    setDraft({});
    setFilters({});
    setPage(1);
  }

  async function handleExport(kind: 'csv' | 'pdf') {
    setExportError(null);
    setExporting(kind);
    try {
      await downloadPiiAccessLogExport(kind, filters, getAccessToken);
    } catch {
      setExportError(kind);
    } finally {
      setExporting(null);
    }
  }

  function handleRetentionSave() {
    const years = Number.parseInt(retentionDraft, 10);
    if (!Number.isFinite(years)) return;
    retentionMut.mutate(years);
  }

  const retentionValue = retentionDraft || String(retentionYears);

  return (
    <section className={`${GS_COMPONENTS.card} flex flex-col gap-5 p-5`}>
      <SectionHeader
        title={t('gest:admin.piiAccessLog.title')}
        description={t('gest:admin.piiAccessLog.description')}
      >
        <span className="text-chip text-slate-400 dark:text-slate-500">
          {t('gest:admin.piiAccessLog.count', { n: total })}
        </span>
      </SectionHeader>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        <div className="flex flex-col gap-1">
          <label htmlFor={idFrom} className="text-chip font-medium text-slate-600 dark:text-slate-300">
            {t('gest:admin.piiAccessLog.filters.from')}
          </label>
          <input
            id={idFrom}
            type="datetime-local"
            className={GS_COMPONENTS.input}
            value={draft.from ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value ? new Date(e.target.value).toISOString() : undefined }))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={idTo} className="text-chip font-medium text-slate-600 dark:text-slate-300">
            {t('gest:admin.piiAccessLog.filters.to')}
          </label>
          <input
            id={idTo}
            type="datetime-local"
            className={GS_COMPONENTS.input}
            value={draft.to ? draft.to.slice(0, 16) : ''}
            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value ? new Date(e.target.value).toISOString() : undefined }))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={idActor} className="text-chip font-medium text-slate-600 dark:text-slate-300">
            {t('gest:admin.piiAccessLog.filters.actor')}
          </label>
          <input
            id={idActor}
            type="text"
            className={GS_COMPONENTS.input}
            value={draft.actorEmail ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, actorEmail: e.target.value || undefined }))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={idSubject} className="text-chip font-medium text-slate-600 dark:text-slate-300">
            {t('gest:admin.piiAccessLog.filters.subject')}
          </label>
          <input
            id={idSubject}
            type="text"
            className={GS_COMPONENTS.input}
            value={draft.subjectQuery ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, subjectQuery: e.target.value || undefined }))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={idField} className="text-chip font-medium text-slate-600 dark:text-slate-300">
            {t('gest:admin.piiAccessLog.filters.field')}
          </label>
          <input
            id={idField}
            type="text"
            className={GS_COMPONENTS.input}
            value={draft.fieldName ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, fieldName: e.target.value || undefined }))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={idRecordType} className="text-chip font-medium text-slate-600 dark:text-slate-300">
            {t('gest:admin.piiAccessLog.filters.recordType')}
          </label>
          <input
            id={idRecordType}
            type="text"
            className={GS_COMPONENTS.input}
            value={draft.recordType ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, recordType: e.target.value || undefined }))}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`} onClick={applyFilters}>
          {t('gest:admin.piiAccessLog.filters.apply')}
        </button>
        <button type="button" className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`} onClick={clearFilters}>
          {t('gest:admin.piiAccessLog.filters.clear')}
        </button>
        <button
          type="button"
          className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5`}
          disabled={exporting !== null}
          onClick={() => void handleExport('csv')}
        >
          <Download size={16} aria-hidden="true" />
          {exporting === 'csv' ? t('gest:admin.piiAccessLog.exporting') : t('gest:admin.piiAccessLog.exportCsv')}
        </button>
        <button
          type="button"
          className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5`}
          disabled={exporting !== null}
          onClick={() => void handleExport('pdf')}
        >
          <FileText size={16} aria-hidden="true" />
          {exporting === 'pdf' ? t('gest:admin.piiAccessLog.exporting') : t('gest:admin.piiAccessLog.exportPdf')}
        </button>
      </div>

      {exportError ? (
        <p className="text-chip text-red-600 dark:text-red-400" role="alert">
          {t(`gest:admin.piiAccessLog.exportError.${exportError}`)}
        </p>
      ) : null}

      <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
        <p className="text-field font-medium text-slate-800 dark:text-slate-100">
          {t('gest:admin.piiAccessLog.retention.title')}
        </p>
        <p className="mt-1 text-chip text-slate-500 dark:text-slate-400">
          {t('gest:admin.piiAccessLog.retention.hint', { min: retentionMinYears, current: retentionYears })}
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label htmlFor={idRetention} className="text-chip font-medium text-slate-600 dark:text-slate-300">
              {t('gest:admin.piiAccessLog.retention.yearsLabel')}
            </label>
            <input
              id={idRetention}
              type="number"
              min={retentionMinYears}
              max={99}
              className={`${GS_COMPONENTS.input} w-28`}
              value={retentionValue}
              onChange={(e) => setRetentionDraft(e.target.value)}
            />
          </div>
          <button
            type="button"
            className={`${GS_COMPONENTS.btnAction} touch-manipulation`}
            disabled={retentionMut.isPending}
            onClick={handleRetentionSave}
          >
            {t('gest:admin.piiAccessLog.retention.save')}
          </button>
        </div>
        {retentionMut.isError ? (
          <p className="mt-2 text-chip text-red-600 dark:text-red-400" role="alert">
            {t('gest:admin.piiAccessLog.retention.error')}
          </p>
        ) : null}
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<Shield size={48} aria-hidden="true" />}
          title={t('gest:admin.piiAccessLog.empty')}
        />
      ) : (
        <>
          <DataTable
            rowKey={(row) => row.id}
            columns={columns}
            data={items}
            emptyMessage={t('gest:admin.piiAccessLog.empty')}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-chip text-slate-500 dark:text-slate-400">
              {t('gest:admin.piiAccessLog.pagination', { page, totalPages })}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('gest:admin.piiAccessLog.prev')}
              </button>
              <button
                type="button"
                className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('gest:admin.piiAccessLog.next')}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
